import {
  Button,
  Form,
  Input,
  Modal,
  Switch,
  Table,
  Tag,
  message,
  notification,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { appVersionsApi, AppVersion } from "../../_helpers/apiService";
import { PageTitle } from "../../components";

const formItemLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};

function AppVersions() {
  const [form] = Form.useForm();
  const [data, setData] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<'android' | 'ios' | null>(null);
  const userData = useSelector((state: any) => state.authentication.userData);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await appVersionsApi.getAll();
      setData(response.data);
    } catch (error: any) {
      console.error("Fetch versions error:", error);
      message.error("Хувилбарын мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const handleCancel = () => {
    setVisible(false);
    setIsEdit(false);
    setSelectedPlatform(null);
    form.resetFields();
  };

  const handleEdit = (record: AppVersion) => {
    setIsEdit(true);
    setSelectedPlatform(record.platform);
    form.setFieldsValue({
      version: record.version,
      forceUpdate: record.force_update,
      updateUrl: record.update_url || '',
    });
    setVisible(true);
  };

  const handleCreate = (platform: 'android' | 'ios') => {
    setIsEdit(false);
    setSelectedPlatform(platform);
    form.setFieldsValue({
      version: '1.0.0',
      forceUpdate: false,
      updateUrl: '',
    });
    setVisible(true);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setConfirmLoading(true);
        try {
          if (isEdit && selectedPlatform) {
            await appVersionsApi.update(selectedPlatform, {
              version: values.version,
              force_update: values.forceUpdate,
              update_url: values.updateUrl || undefined,
            });

            notification.success({
              message: "Амжилттай",
              description: `${selectedPlatform === 'android' ? 'Android' : 'iOS'} хувилбарын мэдээлэл амжилттай шинэчлэгдлээ.`,
            });
          } else if (selectedPlatform) {
            await appVersionsApi.create({
              platform: selectedPlatform,
              version: values.version,
              force_update: values.forceUpdate,
              update_url: values.updateUrl || undefined,
            });

            notification.success({
              message: "Амжилттай",
              description: `${selectedPlatform === 'android' ? 'Android' : 'iOS'} хувилбарын мэдээлэл амжилттай нэмэгдлээ.`,
            });
          }

          setVisible(false);
          setConfirmLoading(false);
          form.resetFields();
          setIsEdit(false);
          setSelectedPlatform(null);
          fetchVersions();
        } catch (error: any) {
          setConfirmLoading(false);
          console.error("Save version error:", error);

          let errorMessage = "Хувилбарын мэдээлэл хадгалахад алдаа гарлаа";
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          }

          notification.error({
            message: "Амжилтгүй",
            description: errorMessage,
          });
        }
      })
      .catch((info) => {
        setConfirmLoading(false);
        console.log("Validate Failed:", info);
      });
  };

  const columns: ColumnsType<AppVersion> = [
    {
      title: "Платформ",
      dataIndex: "platform",
      key: "platform",
      width: 150,
      render: (platform: string) => (
        <Tag color={platform === 'android' ? 'green' : 'blue'}>
          {platform === 'android' ? 'Android' : 'iOS'}
        </Tag>
      ),
    },
    {
      title: "Хувилбар",
      dataIndex: "version",
      key: "version",
      width: 150,
    },
    {
      title: "Албадан шинэчлэх",
      dataIndex: "force_update",
      key: "force_update",
      width: 150,
      render: (forceUpdate: boolean) => (
        <Tag color={forceUpdate ? "red" : "green"}>
          {forceUpdate ? "Тийм" : "Үгүй"}
        </Tag>
      ),
    },
    {
      title: "Шинэчлэх холбоос",
      dataIndex: "update_url",
      key: "update_url",
      render: (url: string) => url || "-",
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record)}>
          Засах
        </Button>
      ),
    },
  ];

  // Only superadmin can access this page
  if (userData.role !== "superadmin") {
    return (
      <>
        <PageTitle title="Апп хувилбар удирдлага" description="Хандах эрх байхгүй" />
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div style={{ textAlign: "center", padding: "50px" }}>
              <h3>Хандах эрхгүй байна</h3>
              <p>Энэ хуудсанд зөвхөн супер админ хандах боломжтой.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const androidVersion = data.find(v => v.platform === 'android');
  const iosVersion = data.find(v => v.platform === 'ios');

  return (
    <>
      <PageTitle
        title="Апп хувилбар удирдлага"
        description="Апп-ын хувилбарын мэдээлэл удирдах"
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  {!androidVersion && (
                    <div style={{ marginBottom: 16 }}>
                      <Button
                        type="primary"
                        onClick={() => handleCreate('android')}
                      >
                        Android хувилбар нэмэх
                      </Button>
                    </div>
                  )}
                  {!iosVersion && (
                    <div style={{ marginBottom: 16 }}>
                      <Button
                        type="primary"
                        onClick={() => handleCreate('ios')}
                      >
                        iOS хувилбар нэмэх
                      </Button>
                    </div>
                  )}
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      rowKey="id"
                      pagination={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={`${isEdit ? 'Засах' : 'Нэмэх'} - ${selectedPlatform === 'android' ? 'Android' : 'iOS'} хувилбар`}
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={isEdit ? 'Хадгалах' : 'Нэмэх'}
        cancelText="Болих"
        width={520}
        confirmLoading={confirmLoading}
      >
        <Form
          {...formItemLayout}
          form={form}
        >
          <Form.Item
            label="Хувилбар"
            name="version"
            rules={[
              { required: true, message: "Хувилбар оруулна уу!" },
              { pattern: /^\d+\.\d+\.\d+$/, message: "Зөв форматаар оруулна уу! (e.g., 1.0.0)" }
            ]}
          >
            <Input placeholder="1.0.0" />
          </Form.Item>
          <Form.Item
            label="Албадан шинэчлэх"
            name="forceUpdate"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Шинэчлэх холбоос"
            name="updateUrl"
          >
            <Input placeholder="https://play.google.com/store/apps/..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default AppVersions;
